/**
 * Cart rewards: tiered discount codes, the free T-shirt option (variant B),
 * the drawer progress bar and the free-tee picker.
 *
 * Runs as a reconciler on window.CartAPI (assets/cart.js): after every cart
 * state change it receives the cart JSON the core already has and returns the
 * /cart/update.js body needed to bring codes and gift lines in line with the
 * tiers, or null. No fetch interception, no polling of /cart.js.
 *
 * CONTROL (A) - default:
 *   $250 - $349.99  ->  15OFF250   (15% off)
 *   $350 - $499.99  ->  20OFF350   (20% off)
 *   $500+           ->  25OFF500   (25% off)
 *
 * VARIANT (B) - ONE REWARD AT A TIME. The free T-shirt is the first rung, and
 * the first percentage tier above it replaces it:
 *   $250 - $349.99  ->  free-tee code only (tee picked in the drawer)
 *   $350 - $499.99  ->  20OFF350 only; the tee line is removed and the picker hidden
 *   $500+           ->  25OFF500 only
 *   The picked tee is a real line item carrying the property `_free_tee`, made $0
 *   by the free-tee code, and removed as soon as the pre-discount subtotal
 *   (excluding the tee itself) leaves the gift window in either direction.
 *   The shopper can also remove it with the line's trash icon; the picker then
 *   returns to the top of the drawer (it sits below the lines while a tee is in
 *   the cart) so another tee can be picked.
 *   The window opens at the "Free T-Shirt unlocks at" section setting, which MUST
 *   match the minimum purchase amount on the Buy X Get Y discount in admin.
 *
 * Assignment: default A. QA overrides via ?cart_reward=A|B|off (consumed once and
 * stripped from the URL) or cartRewards.setVariant('A'|'B'|null); stored under
 * cartRewardPreviewVariant / cartRewardPreviewVariantTs for 24 h. Non-USD carts
 * always run A. Effective variant: window.__cartRewardVariant and
 * <html data-cart-reward-variant>.
 *
 * Shopify admin setup: codes 15OFF250 / 20OFF350 / 25OFF500 (order discounts).
 * For B, a Buy X Get Y discount CODE (default FREETEE, configurable in the
 * cart drawer section): minimum purchase equal to the "Free T-Shirt unlocks at"
 * setting, from a collection containing every sellable product; customer gets 1
 * of the three tees at 100% off, max 1 per order. The tee never rides
 * alongside a tier discount, so no discount combinations are required.
 */

(function () {
  const API = window.CartAPI;
  if (!API) return;

  var TIERS_A = [
    { amount: 25000, discount: '15%', label: 'GET 15% off', type: 'pct', code: '15OFF250' },
    { amount: 35000, discount: '20%', label: 'GET 20% off', type: 'pct', code: '20OFF350' },
    { amount: 50000, discount: '25%', label: 'GET 25% off', type: 'pct', code: '25OFF500' },
  ];
  // Where the gift sits on the ladder. The minimum comes from the cart drawer
  // section setting (see snippets/cart-free-tee-picker.liquid) so it can move
  // without a code change; it MUST match the minimum purchase amount on the Buy
  // X Get Y discount in Shopify admin, or Shopify will not zero the tee.
  // The maximum is derived from the ladder (see giftMax); this is only the
  // fallback for a ladder with no percentage tier above the gift.
  var GIFT_MIN_DEFAULT = 25000;
  var GIFT_MAX_DEFAULT = 50000;
  var GIFT_LABEL = 'T-Shirt';
  var GIFT_CODE_DEFAULT = 'FREETEE';
  var GIFT_PROPERTY = '_free_tee';
  var ATTRIBUTE_KEY = '_cart_reward_variant';
  var MANAGED_CODES = ['15OFF250', '20OFF350', '25OFF500', '12OFF250', '17OFF350', '22OFF500'];

  var DEFAULT_VARIANT = 'A';
  var REWARD_KEY = 'cartRewardPreviewVariant';
  var REWARD_TS_KEY = 'cartRewardPreviewVariantTs';
  var REWARD_TTL_MS = 24 * 60 * 60 * 1000;
  var QA_PARAM = 'cart_reward';

  /* ---------------------------------------------------------------- */
  /* Variant                                                           */
  /* ---------------------------------------------------------------- */
  var override = null;

  function storeVariant(v) {
    try {
      if (v === 'A' || v === 'B') {
        localStorage.setItem(REWARD_KEY, v);
        localStorage.setItem(REWARD_TS_KEY, String(Date.now()));
      } else {
        localStorage.removeItem(REWARD_KEY);
        localStorage.removeItem(REWARD_TS_KEY);
      }
    } catch (e) {}
  }

  function setOverride(v) {
    override = v === 'A' || v === 'B' ? v : null;
    storeVariant(override);
  }

  function readStored() {
    try {
      var stored = localStorage.getItem(REWARD_KEY);
      if (stored !== 'A' && stored !== 'B') return null;
      var ts = parseInt(localStorage.getItem(REWARD_TS_KEY), 10);
      if (!ts || Date.now() - ts > REWARD_TTL_MS) return null;
      return stored;
    } catch (e) {
      return null;
    }
  }

  function consumeQaParam() {
    var qa = null;
    try {
      qa = new URLSearchParams(window.location.search).get(QA_PARAM);
    } catch (e) {}
    if (qa !== 'A' && qa !== 'B' && qa !== 'off') return;
    setOverride(qa === 'off' ? null : qa);
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete(QA_PARAM);
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (e) {}
  }

  function readAssigned() {
    return override || readStored() || DEFAULT_VARIANT;
  }

  consumeQaParam();
  var assigned = readAssigned();
  var effective = assigned;

  function pickerEl() {
    return document.getElementById('CartFreeTee');
  }

  function giftCode() {
    var el = pickerEl();
    var code = el && el.getAttribute('data-free-tee-code');
    return (code && code.trim()) || GIFT_CODE_DEFAULT;
  }

  function giftBound(attr, fallback) {
    var el = pickerEl();
    var cents = el && parseInt(el.getAttribute(attr), 10);
    return cents > 0 ? cents : fallback;
  }

  function giftMin() {
    return giftBound('data-free-tee-min', GIFT_MIN_DEFAULT);
  }

  // One reward at a time: the gift window closes where the first percentage
  // tier above it opens, so a shopper holds the tee or a discount, never both.
  // data-free-tee-max overrides this if the tee should ever stack instead.
  function giftMax() {
    var override = giftBound('data-free-tee-max', 0);
    if (override) return override;
    var min = giftMin();
    var tiers = tiersFor('B');
    var end = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].type !== 'pct' || tiers[i].amount <= min) continue;
      if (!end || tiers[i].amount < end) end = tiers[i].amount;
    }
    return end || GIFT_MAX_DEFAULT;
  }

  function tiersFor(v) {
    if (v === 'B') {
      return [
        { amount: giftMin(), discount: GIFT_LABEL, label: GIFT_LABEL, type: 'gift', code: giftCode() },
        { amount: 35000, discount: '20%', label: 'GET 20% off', type: 'pct', code: '20OFF350' },
        { amount: 50000, discount: '25%', label: 'GET 25% off', type: 'pct', code: '25OFF500' },
      ];
    }
    return TIERS_A.slice();
  }

  function applyEffective(v) {
    effective = v;
    window.__cartRewardVariant = v;
    window.__autoDiscountTiers = tiersFor(v);
    try {
      document.documentElement.setAttribute('data-cart-reward-variant', v);
    } catch (e) {}
  }
  applyEffective(effective);

  function refreshAssigned() {
    var v = readAssigned();
    if (v === assigned) return false;
    assigned = v;
    return true;
  }

  function effectiveFor(cart) {
    return assigned === 'B' && (!cart || !cart.currency || cart.currency === 'USD') ? 'B' : 'A';
  }

  /* ---------------------------------------------------------------- */
  /* Cart helpers                                                      */
  /* ---------------------------------------------------------------- */
  // The embedded cart state resolves `gift` in Liquid, because line item
  // properties are a list of first/last pairs there rather than a hash.
  // Ajax API responses carry real properties instead.
  function isGiftLine(item) {
    return !!(item && (item.gift === true || (item.properties && item.properties[GIFT_PROPERTY])));
  }

  function freeTeeLines(cart) {
    return ((cart && cart.items) || []).filter(isGiftLine);
  }

  function tierSubtotal(cart) {
    var total = (cart && cart.original_total_price) || 0;
    freeTeeLines(cart).forEach(function (i) {
      total -= i.original_line_price || 0;
    });
    return Math.max(0, total);
  }
  window.__cartTierSubtotal = tierSubtotal;

  function giftEligible(cents) {
    return cents >= giftMin() && cents < giftMax();
  }
  window.__cartGiftEligible = giftEligible;

  function pctCodeFor(cents) {
    var tiers = tiersFor(effective);
    var code = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].type === 'pct' && cents >= tiers[i].amount) code = tiers[i].code;
    }
    return code;
  }

  function codeList(cart) {
    return ((cart && cart.discount_codes) || [])
      .map(function (c) {
        return String(c.code || '').toUpperCase();
      })
      .filter(Boolean);
  }

  function isManaged(code) {
    return MANAGED_CODES.indexOf(code) > -1 || code === giftCode().toUpperCase();
  }

  function sameSet(a, b) {
    if (a.length !== b.length) return false;
    var seen = {};
    a.forEach(function (c) {
      seen[c] = true;
    });
    return b.every(function (c) {
      return seen[c];
    });
  }

  function giftApplicable(cart) {
    var code = giftCode().toUpperCase();
    var applicable = ((cart && cart.discount_codes) || []).some(function (c) {
      return String(c.code || '').toUpperCase() === code && c.applicable === true;
    });
    return (
      applicable &&
      freeTeeLines(cart).every(function (line) {
        return line.final_line_price === 0;
      })
    );
  }

  function zeroLines(lines, keepOne) {
    var updates = {};
    lines.forEach(function (line, index) {
      updates[line.key] = keepOne && index === 0 ? 1 : 0;
    });
    return updates;
  }

  /* ---------------------------------------------------------------- */
  /* Reconciler: returns the /cart/update.js body needed, or null      */
  /* ---------------------------------------------------------------- */
  var giftUnavailable = false;
  var giftRetry = false;
  var giftWarned = false;

  function reportGiftUnavailable(state) {
    if (giftUnavailable === state) return;
    giftUnavailable = state;
    // Reveals the real price on gift lines (see the free gift block in
    // assets/cart.css). Until this is set they read FREE.
    try {
      document.documentElement.toggleAttribute('data-cart-gift-unavailable', state);
    } catch (e) {}
    if (state && !giftWarned) {
      giftWarned = true;
      if (window.console && console.warn) {
        console.warn('[cart-rewards] "' + giftCode() + '" is on the cart but is not making the selected T-shirt free. ' +
          'Check the Buy X Get Y discount in Shopify admin: minimum spend, the collection it targets, the three tees it gives, ' +
          'and that it combines with ' + (pctCodeFor(35000) || 'the tier codes') + '.');
      }
    }
    if (!state) giftWarned = false;
    renderPicker(API.state || lastCart);
  }
  var lastCart = null;

  function reconcile(cart) {
    refreshAssigned();
    var eff = effectiveFor(cart);
    if (eff !== effective) {
      applyEffective(eff);
      decorate(cart);
    }
    lastCart = cart;

    var body = {};
    var lines = freeTeeLines(cart);
    var sub = tierSubtotal(cart);
    var existing = codeList(cart);
    var foreign = existing.filter(function (c) {
      return !isManaged(c);
    });
    var target;

    if (eff === 'A') {
      if (lines.length) body.updates = zeroLines(lines, false);
      var code = pctCodeFor(sub);
      window.__autoDiscountCode = code;
      window.__cartRewardCodes = code ? [code] : [];
      target = foreign.slice();
      if (code) target.push(code);
      if (!sameSet(existing, target)) body.discount = target.join(',');
      // Variant A strips gift lines entirely, so the flag can never apply here.
      reportGiftUnavailable(false);
    } else {
      window.__autoDiscountCode = null;
      var keepOne = giftEligible(sub);
      var needsUpdate = lines.length && (!keepOne || lines.length > 1 || lines[0].quantity !== 1);
      if (needsUpdate) body.updates = zeroLines(lines, keepOne);
      var linesAfter = needsUpdate ? (keepOne ? [lines[0]] : []) : lines;

      var desired = [];
      var pct = pctCodeFor(sub);
      if (pct) desired.push(pct.toUpperCase());
      if (linesAfter.length && keepOne) desired.push(giftCode().toUpperCase());
      window.__cartRewardCodes = desired.slice();
      target = desired.concat(
        foreign.filter(function (c) {
          return desired.indexOf(c) === -1;
        })
      );

      // Re-submitting an already-attached code only helps right after the
      // shopper picked a size (the discount may have been fixed since). Doing
      // it on every pass would loop on a genuinely misconfigured discount.
      var retry = linesAfter.length && !giftApplicable(cart) && giftRetry;
      if (!sameSet(existing, target) || retry) body.discount = target.join(',');
      giftRetry = false;

      if (!Object.keys(body).length && linesAfter.length && !giftApplicable(cart)) {
        // Partial state cannot see a code that is attached but not applying,
        // so never call the gift broken from it: check the real cart instead.
        if (cart.partial) return API.VERIFY;
        reportGiftUnavailable(true);
        return null;
      }
      reportGiftUnavailable(false);
    }

    // Stamp the variant on the cart for order segmentation. Piggybacks on an
    // update that is happening anyway; otherwise the checkout guard stamps it.
    if (Object.keys(body).length && needsStamp(cart)) body.attributes = stampBody();

    return Object.keys(body).length ? body : null;
  }

  function needsStamp(cart) {
    return !!(cart && cart.item_count && (cart.attributes || {})[ATTRIBUTE_KEY] !== effective);
  }

  function stampBody() {
    var attrs = {};
    attrs[ATTRIBUTE_KEY] = effective;
    return attrs;
  }

  API.addReconciler(reconcile);

  // Best effort: assets/cart.js goes to checkout even if this fails.
  API.addCheckoutGuard(function (cart) {
    if (needsStamp(cart)) return API.update({ attributes: stampBody() });
  });

  /* ---------------------------------------------------------------- */
  /* Progress bar (drawer)                                             */
  /* ---------------------------------------------------------------- */
  var money = null;
  function formatCents(cents) {
    if (!money) {
      try {
        money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      } catch (e) {
        money = { format: function (n) { return '$' + n.toFixed(2); } };
      }
    }
    return money.format(cents / 100);
  }

  function rewardMessageHtml(tiers, cartTotal, next) {
    var gift = tiers.find(function (t) { return t.type === 'gift'; });
    var top = tiers[tiers.length - 1];
    var giftEnd = giftMax();
    var giftUnlocked = !!gift && cartTotal >= gift.amount && cartTotal < giftEnd;
    var amount = next ? '<span class="amount-needed">' + formatCents(next.amount - cartTotal) + '</span>' : '';
    if (next && next.type === 'gift') {
      return 'You are ' + amount + ' away from getting a <span class="discount-percentage">FREE ' + next.label + '</span>';
    }
    if (next && giftUnlocked) {
      // The tier that closes the gift window replaces it; anything below stacks.
      var replaces = next.amount >= giftEnd;
      return '<span class="discount-percentage">Congrats!</span> You unlocked a <span class="discount-percentage">FREE ' + gift.label +
        '!</span> Spend ' + amount + ' more to ' + (replaces ? 'unlock' : 'add') +
        ' <span class="discount-percentage">' + next.discount + ' OFF</span>' + (replaces ? ' instead.' : '.');
    }
    if (next) {
      return 'You are ' + amount + ' away from getting a <span class="discount-percentage">' + next.discount + ' discount</span>';
    }
    return '<span class="discount-percentage">Congrats!</span> You unlocked <span class="discount-percentage">' + top.discount + ' OFF</span>!';
  }

  function controlMessageHtml(tiers, cartTotal, next) {
    if (next) {
      return 'You are <span class="amount-needed">' + formatCents(next.amount - cartTotal) + '</span> away from getting a <span class="discount-percentage">' + next.discount + ' discount</span>';
    }
    return 'Congratulations you got <span class="discount-percentage"> ' + tiers[tiers.length - 1].discount + ' off discount</span>';
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  /* ---------------------------------------------------------------- */
  /* Unlock celebration                                                */
  /*                                                                   */
  /* A step is celebrated only when it goes from not-reached to        */
  /* reached, so re-renders and downgrades stay quiet. The style comes */
  /* from the theme editor (data-unlock-anim) and the work is done by  */
  /* assets/cart.css; here we only flip data-js-unlock, which survives */
  /* DOM patches (the morph never strips data-js-* attributes).        */
  /* ---------------------------------------------------------------- */
  var UNLOCK_MS = 1000;
  var reached = null; // per step: was it reached at the previous render?

  function animationName(root) {
    return (root && root.getAttribute('data-unlock-anim')) || 'pop';
  }

  function motionOk() {
    try {
      return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return true;
    }
  }

  function celebrate(el, attr) {
    if (!el) return;
    el.removeAttribute(attr);
    void el.offsetWidth; // restart the animation if one is already running
    el.setAttribute(attr, '');
    clearTimeout(el.__cartUnlockTimer);
    el.__cartUnlockTimer = setTimeout(function () {
      el.removeAttribute(attr);
    }, UNLOCK_MS);
  }

  // Replays every reached step, for the "replay on every cart open" setting.
  function replayUnlocks() {
    var root = document.querySelector('.discount-progress');
    if (!root || animationName(root) === 'none' || !motionOk()) return;
    if (root.getAttribute('data-replay-on-open') !== 'true') return;
    var steps = root.querySelectorAll('.progress-step');
    var celebrated = false;
    steps.forEach(function (step, index) {
      if (!step.classList.contains('completed') || step.classList.contains('superseded')) return;
      setTimeout(function () {
        celebrate(step, 'data-js-unlock');
      }, index * 110);
      celebrated = true;
    });
    if (celebrated) celebrate(root.querySelector('.discount-message'), 'data-js-bump');
  }

  document.addEventListener('cart:drawer-opened', replayUnlocks);

  function renderProgressBar(cart) {
    var root = document.querySelector('.discount-progress');
    if (!root || !cart) return;
    var messageEl = root.querySelector('.discount-message');
    var steps = root.querySelectorAll('.progress-step');
    var line = root.querySelector('.progress-line');
    if (!messageEl || !line || !steps.length) return;

    var tiers = tiersFor(effective);
    var total = tierSubtotal(cart);
    var next = tiers.find(function (t) { return total < t.amount; });
    var hasGift = tiers.some(function (t) { return t.type === 'gift'; });

    var html = hasGift ? rewardMessageHtml(tiers, total, next) : controlMessageHtml(tiers, total, next);
    if (messageEl.innerHTML !== html) messageEl.innerHTML = html;

    var pct = 0;
    if (total >= tiers[0].amount) {
      for (var i = 0; i < tiers.length; i++) {
        if (total >= tiers[i].amount) {
          pct = ((i + 1) / tiers.length) * 100;
        } else {
          var prev = tiers[i - 1] || { amount: 0 };
          var range = tiers[i].amount - prev.amount;
          pct = (i / tiers.length) * 100 + ((total - prev.amount) / range) * (100 / tiers.length);
          break;
        }
      }
    }
    var width = pct.toFixed(0) + '%';
    if (line.style.width !== width) line.style.width = width;

    var giftEnd = giftMax();
    var animate = animationName(root) !== 'none' && motionOk();
    var known = reached; // null on the very first render: nothing to celebrate yet
    var now = [];
    var unlocked = false;

    steps.forEach(function (step, index) {
      var tier = tiers[index];
      if (!tier) return;
      if (step.getAttribute('data-discount') !== tier.discount) step.setAttribute('data-discount', tier.discount);
      if (step.getAttribute('data-tier-type') !== tier.type) step.setAttribute('data-tier-type', tier.type);
      // The gift circle reads "FREE" over "T-Shirt"; assets/cart.css keeps the line break.
      setText(step.querySelector('span'), tier.type === 'gift' ? 'FREE\n' + tier.label : tier.label);
      var superseded = tier.type === 'gift' && total >= giftEnd;
      step.classList.toggle('superseded', superseded);
      var completed = total >= tier.amount;
      var active = !completed && !!next && tier.amount === next.amount;
      step.classList.toggle('completed', completed);
      step.classList.toggle('active', active);

      now[index] = completed && !superseded;
      if (animate && known && now[index] && !known[index]) {
        celebrate(step, 'data-js-unlock');
        unlocked = true;
      }
    });
    reached = now;
    if (unlocked) celebrate(messageEl, 'data-js-bump');
  }

  /* ---------------------------------------------------------------- */
  /* Free T-shirt picker (markup: snippets/cart-free-tee-picker.liquid) */
  /* ---------------------------------------------------------------- */
  var pickerProduct = null;
  var selecting = false;
  var pickerError = '';
  var pickerDataCache = null;

  function pickerData() {
    var el = document.querySelector('#CartFreeTee [data-free-tee-data]');
    if (!el) return [];
    if (pickerDataCache && pickerDataCache.el === el) return pickerDataCache.data;
    var data = [];
    try {
      data = JSON.parse(el.textContent);
      if (!Array.isArray(data)) data = [];
    } catch (e) {
      data = [];
    }
    pickerDataCache = { el: el, data: data };
    return data;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function setPickerBusy(busy) {
    var el = document.getElementById('CartFreeTee');
    if (el) el.classList.toggle('is-busy', !!busy);
  }

  function setPickerHint(text) {
    setText(document.querySelector('#CartFreeTee [data-free-tee-hint]'), text);
  }

  // The picker leads the drawer until a tee is in the cart, then it follows the
  // line items so the added tee (and the rest of the cart) come first; removing
  // the tee puts it back at the top. The morph restores server order (picker
  // before lines) on every re-render, and this runs right after it in the same
  // synchronous cart:state pass, so the move never paints twice.
  function placePicker(el, added) {
    var body = el.parentElement;
    if (!body) return;
    var lines = null;
    for (var n = body.firstElementChild; n; n = n.nextElementSibling) {
      if (n.getAttribute('data-key') === 'lines') {
        lines = n;
        break;
      }
    }
    if (!lines) return;
    var placed = added ? el.previousElementSibling === lines : el.nextElementSibling === lines;
    if (!placed) body.insertBefore(el, added ? lines.nextSibling : lines);
    if (el.hasAttribute('data-js-added') !== !!added) el.toggleAttribute('data-js-added', !!added);
  }

  function renderPicker(cart, optimisticVariant) {
    var el = document.getElementById('CartFreeTee');
    if (!el) return;
    if (effective !== 'B' || !cart || !giftEligible(tierSubtotal(cart))) {
      el.hidden = true;
      return;
    }
    var data = pickerData();
    if (!data.length) {
      el.hidden = true;
      return;
    }

    var lines = freeTeeLines(cart);
    var inCart = lines.length ? lines[0] : null;
    var wanted = pickerProduct || (inCart && inCart.product_id) || data[0].id;
    var product = data.find(function (p) { return p.id === wanted; }) || data[0];
    placePicker(el, !!inCart);

    el.querySelectorAll('[data-free-tee-product]').forEach(function (card) {
      var pressed = Number(card.getAttribute('data-free-tee-product')) === product.id ? 'true' : 'false';
      if (card.getAttribute('aria-pressed') !== pressed) card.setAttribute('aria-pressed', pressed);
    });

    // Sold-out sizes are left out entirely rather than shown as disabled chips.
    var available = (product.variants || []).filter(function (v) { return v.available; });
    var sizes = el.querySelector('[data-free-tee-sizes]');
    if (sizes) {
      var selectedId = optimisticVariant || (inCart && inCart.variant_id);
      var html = available
        .map(function (v) {
          var selected = selectedId === v.id;
          return (
            '<li><button type="button" class="cart-free-tee__size" data-free-tee-variant="' + v.id + '"' +
            ' aria-pressed="' + (selected ? 'true' : 'false') + '"' +
            ' aria-label="Size ' + escapeHtml(v.size) + '">' +
            escapeHtml(v.size) + '</button></li>'
          );
        })
        .join('');
      if (sizes.innerHTML !== html) sizes.innerHTML = html;
    }

    if (!available.length) {
      setPickerHint('This tee is sold out. Pick another one.');
    } else if (pickerError) {
      setPickerHint(pickerError);
    } else if (selecting) {
      setPickerHint('Adding your free tee…');
    } else if (giftUnavailable) {
      setPickerHint('The free offer couldn’t be applied. Your T-shirt is in the cart at the price shown. Select a size to retry.');
    } else if (inCart) {
      setPickerHint(inCart.product_id === product.id ? 'Added to your cart. Pick another size or tee to swap it.' : 'Pick a size to swap your free tee for this one.');
    } else {
      setPickerHint('Pick a size to add it to your cart.');
    }

    el.hidden = false;
  }

  function selectFreeTee(variantId) {
    if (selecting || effective !== 'B') return Promise.resolve();
    var eligible = pickerData().some(function (product) {
      return product.variants.some(function (v) { return v.id === variantId && v.available; });
    });
    if (!eligible) return Promise.resolve();

    selecting = true;
    pickerError = '';
    giftUnavailable = false;
    giftRetry = true;
    setPickerBusy(true);
    renderPicker(lastCart, variantId);

    return API.run(async function () {
      var cart = API.state;
      if (!cart || readAssigned() !== 'B' || (cart.currency && cart.currency !== 'USD') || !giftEligible(tierSubtotal(cart))) return;
      var lines = freeTeeLines(cart);
      if (lines.length === 1 && lines[0].variant_id === variantId && lines[0].quantity === 1) {
        // Already the right line: this is the shopper retrying after the
        // discount failed, so re-run against the real cart to resubmit it.
        return API.verifyNow();
      }
      var props = {};
      props[GIFT_PROPERTY] = '1';
      if (lines.length) {
        // Swap: drop the old tee first (no re-render; the add below returns the HTML).
        await API.request(API.routes.cart_update_url, { body: { updates: zeroLines(lines, false) } });
      }
      await API.mutate(API.routes.cart_add_url, { items: [{ id: variantId, quantity: 1, properties: props }] }, { fullCart: false });
    })
      .catch(function (err) {
        pickerError = err.message;
      })
      .then(function () {
        selecting = false;
        setPickerBusy(false);
        renderPicker(API.state || lastCart);
      });
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var card = e.target.closest('#CartFreeTee [data-free-tee-product]');
    if (card) {
      pickerProduct = Number(card.getAttribute('data-free-tee-product'));
      renderPicker(API.state || lastCart);
      return;
    }
    var chip = e.target.closest('#CartFreeTee [data-free-tee-variant]');
    if (chip) {
      e.preventDefault();
      selectFreeTee(Number(chip.getAttribute('data-free-tee-variant')));
    }
  });

  /* ---------------------------------------------------------------- */
  /* Banner copy ("up to NN%") follows the top percentage tier          */
  /* ---------------------------------------------------------------- */
  function maxPct() {
    var tiers = tiersFor(effective);
    var pct = '25%';
    for (var i = 0; i < tiers.length; i++) if (tiers[i].type === 'pct') pct = tiers[i].discount;
    return pct;
  }

  var bannerVariant = null;
  function updateBannerCopy() {
    if (bannerVariant === effective) return;
    bannerVariant = effective;
    var upToRe = /up\s*to\s*\d+%/gi;
    var max = maxPct();
    document.querySelectorAll('.cc-section-announcement-text-4in1, .bm-footnote, .discounts-note-text').forEach(function (rootEl) {
      var walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var replaced = node.nodeValue.replace(upToRe, 'up to ' + max);
        if (replaced !== node.nodeValue) node.nodeValue = replaced;
      }
    });
  }

  /* ---------------------------------------------------------------- */
  /* UI sync                                                           */
  /* ---------------------------------------------------------------- */
  function decorate(cart) {
    cart = cart || API.state || lastCart;
    lastCart = cart;
    renderProgressBar(cart);
    renderPicker(cart);
    updateBannerCopy();
  }

  document.addEventListener('cart:state', function (e) {
    var cart = e.detail && e.detail.cart;
    var eff = effectiveFor(cart);
    if (eff !== effective) applyEffective(eff);
    decorate(cart);
    document.dispatchEvent(new CustomEvent('cart:auto-discount-applied', { detail: { cart: cart } }));
  });

  window.cartRewards = {
    get variant() { return effective; },
    get assigned() { return assigned; },
    setVariant: function (v) {
      var want = v === 'B' ? 'B' : v === 'A' ? 'A' : null;
      setOverride(want);
      refreshAssigned();
      return API.verify()
        .then(function () {
          if (want && effective !== want && window.console && console.warn) {
            console.warn('[cart-rewards] Asked for variant ' + want + ' but "' + effective + '" is running: variant B needs a USD cart.');
          }
          return effective;
        });
    },
    sync: function () { return API.verify(); },
    tierSubtotal: tierSubtotal,
    giftEligible: giftEligible,
    freeTeeLines: freeTeeLines,
  };

  // The A/B tool may write the flag after this script ran: watch briefly.
  var polls = 0;
  var pollTimer = setInterval(function () {
    if (++polls > 24) clearInterval(pollTimer);
    if (refreshAssigned()) API.verify().catch(function () {});
  }, 250);

  if (API.state) decorate(API.state);
})();
